/**
 * Volume / promo details modal (La Tostadora–style “más info”).
 * Can show live WCDPD tiers + membership / print compares, plus optional custom copy.
 */
import type { Money } from '@ox/schema';

function formatMoney(m: Money): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: m.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(m.amount);
  } catch {
    return `${m.currency} ${m.amount.toFixed(2)}`;
  }
}

export type PromoVolumeTier = {
  from: number;
  to?: number | null;
  label: string;
  unitPrice: Money;
  regularPrice?: Money;
  discountLabel?: string;
  kind?: string;
};

export type PromoMembership = {
  active: boolean;
  regularPrice: Money;
  memberPrice: Money;
};

export type PromoPrintTier = {
  maxSideCm: number;
  label: string;
  regularPrice: Money;
  memberPrice: Money;
};

export function PromoModal({
  title,
  html,
  lines,
  footnote,
  showCustomText = true,
  volumeTiers,
  membership,
  printTiers,
  onClose,
}: {
  title: string;
  html?: string;
  lines?: string[];
  footnote?: string;
  showCustomText?: boolean;
  volumeTiers?: PromoVolumeTier[];
  membership?: PromoMembership | null;
  printTiers?: PromoPrintTier[];
  onClose: () => void;
}) {
  const rich = showCustomText ? html?.trim() : '';
  const showLines = showCustomText && !rich && lines && lines.length > 0;
  const showMembership = !!membership?.active;
  const showPrint = !!printTiers && printTiers.length > 0;
  const showTiers = !!volumeTiers && volumeTiers.length > 0;
  const hasLive = showMembership || showPrint || showTiers;
  const hasCustom = !!(rich || showLines || (showCustomText && footnote));
  const isSimpleOffer = showTiers && volumeTiers!.every((t) => t.kind === 'simple');
  const showDiscountCol = showTiers && volumeTiers!.some((t) => t.discountLabel);
  const showRegularCol = showTiers && volumeTiers!.some((t) => t.regularPrice);

  return (
    <div className="ox-modal-backdrop" onClick={onClose}>
      <div
        className="ox-modal ox-promo-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={title}
      >
        <header>
          <h2>{title}</h2>
          <button type="button" onClick={onClose} aria-label="Cerrar">
            ✕
          </button>
        </header>
        <div className="ox-promo-modal-body">
          {showMembership && membership && (
            <section className="ox-promo-live ox-promo-membership">
              <h3>Tu precio de miembro</h3>
              <div className="ox-promo-membership-row">
                <span className="ox-promo-membership-regular">
                  {formatMoney(membership.regularPrice)}
                </span>
                <span className="ox-promo-membership-member">
                  {formatMoney(membership.memberPrice)}
                </span>
              </div>
              <p className="ox-promo-membership-hint">Precio de catálogo frente a tu precio de miembro.</p>
            </section>
          )}

          {showPrint && (
            <section className="ox-promo-live ox-promo-print">
              <h3>Precios de impresión adicional</h3>
              <table className="ox-promo-tiers-table">
                <thead>
                  <tr>
                    <th>Tamaño</th>
                    <th>Cliente</th>
                    <th>Miembro</th>
                  </tr>
                </thead>
                <tbody>
                  {printTiers!.map((tier) => {
                    const cheaper = tier.memberPrice.amount < tier.regularPrice.amount - 0.00001;
                    return (
                      <tr key={tier.maxSideCm}>
                        <td>{tier.label}</td>
                        <td
                          className={
                            cheaper ? 'ox-promo-print-regular' : undefined
                          }
                        >
                          {formatMoney(tier.regularPrice)}
                        </td>
                        <td
                          className={
                            cheaper ? 'ox-promo-print-member' : undefined
                          }
                        >
                          {formatMoney(tier.memberPrice)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="ox-promo-membership-hint">
                Cargo por vista impresa no incluida, según el lado mayor del diseño.
              </p>
            </section>
          )}

          {showTiers && (
            <section className="ox-promo-live ox-promo-tiers">
              <h3>{isSimpleOffer ? 'Descuento' : 'Descuentos por cantidad'}</h3>
              <table className="ox-promo-tiers-table">
                <thead>
                  <tr>
                    <th>Cantidad</th>
                    {showRegularCol && <th>Antes</th>}
                    <th>Precio / ud.</th>
                    {showDiscountCol && <th>Dto.</th>}
                  </tr>
                </thead>
                <tbody>
                  {volumeTiers!.map((tier) => (
                    <tr key={`${tier.from}-${tier.to ?? 'inf'}`}>
                      <td>{tier.label}</td>
                      {showRegularCol && (
                        <td className="ox-promo-tier-regular">
                          {tier.regularPrice ? formatMoney(tier.regularPrice) : '—'}
                        </td>
                      )}
                      <td className="ox-promo-tier-price">{formatMoney(tier.unitPrice)}</td>
                      {showDiscountCol && (
                        <td className="ox-promo-tier-discount">{tier.discountLabel || '—'}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {isSimpleOffer && (
                <p className="ox-promo-membership-hint">
                  Promoción vigente (tiene prioridad sobre descuentos por volumen).
                </p>
              )}
            </section>
          )}

          {hasLive && hasCustom && <hr className="ox-promo-divider" />}

          {rich ? (
            <div className="ox-rich-html" dangerouslySetInnerHTML={{ __html: rich }} />
          ) : (
            showLines && (
              <>
                <ul>
                  {lines!.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                {showCustomText && footnote && (
                  <p className="ox-promo-modal-foot">{footnote}</p>
                )}
              </>
            )
          )}

          {!hasLive && !hasCustom && (
            <p className="ox-promo-modal-foot">No hay ofertas disponibles en este momento.</p>
          )}
        </div>
      </div>
    </div>
  );
}
